import {yupResolver} from "@hookform/resolvers/yup";
import React, {useEffect, useState} from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";

//import model
import {regionList} from "../../models/commonData";
import {IPProject} from "../../models/IPProject";
import {projectSchema} from "../../validation/projectSchema";

//import redux
import {useDispatch, useSelector} from "react-redux";
import {createFactoryAction, getFactoryList} from "../../features/sliceReducer/factoryListReducer";
import {modalOpenAction} from "../../features/sliceReducer/interactionReducer";
import {RootState} from "../../features/store";

//import material-UI
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import AddToPhotosIcon from "@material-ui/icons/AddToPhotos";
import BackupIcon from "@material-ui/icons/Backup";
import BorderColorIcon from "@material-ui/icons/BorderColor";

//import component
import {useNavigate} from "react-router-dom";
import IPUtility from "../../classes/IPUtility";
import AddParticipant from "../../components/AddParticipant";
import AuditMembers from "../../components/AuditMembers";
import AuditMembersModal from "../../components/AuditMembersModal";
import AuditPlan from "../../components/AuditPlan";
import AuditPlanModal from "../../components/AuditPlanModal";
import CompareProject from "../../components/CompareProject";
import ListTitle from "../../components/ListTitle";
import {getMyProject, setServerSavedAction} from "../../features/sliceReducer/myProjectReducer";
import {createProject, updateProject} from "../../features/sliceReducer/projectReducer";
import {initializeAuditMember} from "../../models/IPAuditMember";
import {initializeIPSchedule} from "../../models/IPSchedule";
import FactoryModalForm from "../factory/FactoryModalForm";
import AddMemberModal from "../user/AddMemberModal";
import AddProjectModal from "./AddProjectModal";

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      display: "flex",
      padding: "32px",
      justifyContent: "spaceBetween",
    },
    formGrid: {
      margin: "0 20px",
      border: "1px solid #dbdce0",
      borderColor: theme.palette.type === "dark" ? "#384347" : "#dbdce0",
      " & h6": {
        padding: "16px",
        backgroundColor: theme.palette.type === "dark" ? "#384347" : "#dbdce0",

        marginBottom: "20px",
      },
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    labelAlign: {
      display: "flex",
      justifyContent: "start",
      alignItems: "center",
      flexBasis: "50%",
      padding: "8px 0 8px 12px",

      "& > label": {
        width: "180px",
        textAlign: "right",
        marginRight: "26px",
      },
    },
    inputSize: {
      width: "300px",
      backgroundColor: theme.palette.input.main,
    },
    memberList: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      margin: " 20px 50px",
      "& div": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 50px 20px 0",
        borderBottom: "1px solid #dbdce0",
        color: theme.palette.primary.dark,
        "& p": {
          padding: "0 20px 0 40px",
          fontSize: "16px",
        },
      },
    },
    detail: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      "& table": {
        width: "90%",
      },

      "& tr, th, td": {
        border: "1px solid grey",
        padding: "12px",
      },
    },
    detailTable: {
      flexGrow: 1,
    },
  })
);

const ManageProject = () => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {currentProject, projectEditSwitch} = useSelector((state: RootState) => state.project);
  const {userToken} = useSelector((state: RootState) => state.auth);
  const {factoryList} = useSelector((state: RootState) => state.factoryList);
  const {myProject} = useSelector((state: RootState) => state.myProject);

  const [fileName, setFileName] = useState(currentProject.uploadedFileName);
  const [target, setTarget] = useState(currentProject.target);
  const [isCompareYear, setIsCompareYear] = useState(currentProject.isCompareYear);
  const [isCompareByScore, setIsCompareByScore] = useState(currentProject.isCompareByScore);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: {errors},
  } = useForm<IPProject>({
    resolver: yupResolver(projectSchema),
  });

  useEffect(() => {
    dispatch(getFactoryList({userToken, rowCount: 0}));
  }, []);

  useEffect(() => {
    if (currentProject) {
      reset({
        ...currentProject,
        endDate: formatDate(currentProject.endDate),
        startDate: formatDate(currentProject.startDate),
      });
    }
  }, [currentProject, reset]);

  const updateCallback = () => {
    // dispatch(getProjectList({ userToken, isAll: false }));
    navigate("/inspect/");
  };
  const onSubmit: SubmitHandler<IPProject> = (data) => {
    data.target = target;
    data.isCompareYear = isCompareYear;
    data.isCompareByScore = isCompareByScore;
    data.isManualPoint = getValues("isManualPoint");
    data.hasCompare = getValues("hasCompare");
    data.participantsUsers = currentProject.participantsUsers;
    data.participantsUIDs = currentProject.participantsUIDs;
    data.auditMembers = currentProject.auditMembers;
    data.compareProjectUIDs = currentProject.compareProjectUIDs;
    data.schedules = currentProject.schedules;
    projectEditSwitch
      ? dispatch(updateProject({userToken, item: data, successHandler: updateCallback}))
      : dispatch(createProject({userToken, item: data, successHandler: updateCallback}));
    // console.log("submit", { ...currentProject, ...data });
    // 편집 프로젝트와 로딩된 마이프로젝트가 같으면 다시 로딩
    if (projectEditSwitch && myProject?.uid == data.uid) {
      IPUtility.log("Reload");
      dispatch(
        getMyProject({
          userToken,
          id: data.uid,
          successHandler: () => dispatch(setServerSavedAction(0)),
        })
      );
    }
  };

  // note fileLoaded
  const fileLoaded = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const fileList = e.target.files;
    if (fileList !== undefined && fileList !== null && fileList.length > 0) {
      var file = fileList[0];
      IPUtility.getFile(file).then((fileInfoData) => {
        const fileName = fileInfoData.fileName;
        const base64 = "data:" + fileInfoData.fileType + ";base64," + fileInfoData.base64StringFile;
        setValue<"uploadedFileName">("uploadedFileName", fileName);
        setValue<"uploadedFileBase64">("uploadedFileBase64", base64);
        setFileName(fileName);
        console.log(base64);
      });
    }
  };

  let currentYear = new Date().getFullYear();
  const yearList: number[] = [];
  for (let i = currentYear; i >= 2018; i--) {
    yearList.push(i);
  }

  const [editSchedule, setEditSchedule] = useState({...initializeIPSchedule});
  const [editAuditMember, setEditAuditMember] = useState({...initializeAuditMember});

  const formatDate = (date: Date | string) => {
    if (typeof date === "string") return date.split("T")[0];
    return date.toISOString().split("T")[0];
  };

  return (
    <>
      {/* <AlertsDialog /> */}
      <AddMemberModal />
      <AuditPlanModal projectUID={currentProject.uid} schedule={editSchedule} />
      <AuditMembersModal auditMember={editAuditMember} />
      <AddProjectModal />
      <FactoryModalForm />

      <form>
        <ListTitle
          subTitle={projectEditSwitch ? "자체감사 프로젝트 수정" : "자체감사 프로젝트 작성"}
        ></ListTitle>
        <Paper className={classes.formContainer}>
          <Box width="42%" className={classes.formGrid}>
            <Typography variant="subtitle1">
              <BorderColorIcon style={{marginRight: "8px", verticalAlign: "middle"}} />
              PSM 프로젝트 정보
            </Typography>
            <Box className={classes.labelAlign}>
              <InputLabel>사업장</InputLabel>
              <Controller
                name="factoryUID"
                control={control}
                defaultValue={currentProject.factoryUID}
                render={({field}) => (
                  <Select
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    margin="dense"
                  >
                    {factoryList.map((factory, index) => (
                      <MenuItem key={factory.uid} value={factory.uid}>
                        {factory.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <Box ml={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    dispatch(createFactoryAction());
                    dispatch(modalOpenAction());
                  }}
                  endIcon={<AddToPhotosIcon>send</AddToPhotosIcon>}
                >
                  사업장 추가
                </Button>
              </Box>
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel htmlFor="name">프로젝트명</InputLabel>
              <Controller
                name="name"
                control={control}
                defaultValue={currentProject.name}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    required
                    placeholder="ex) ex) TDI 물질 취급공정"
                    margin="dense"
                    error={errors.name ? true : false}
                  />
                )}
              />
              <Typography variant="inherit" color="textSecondary">
                {errors.name?.message}
              </Typography>
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>대상구분</InputLabel>
              <Controller
                name="target"
                control={control}
                render={({field}) => (
                  <RadioGroup
                    {...field}
                    className={classes.inputSize}
                    row
                    aria-label="target"
                    value={target}
                    onChange={(e) => {
                      setTarget(parseInt(e.target.value));
                    }}
                  >
                    <FormControlLabel value={0} control={<Radio />} label="업종대상" />
                    <FormControlLabel value={1} control={<Radio />} label="물질대상" />
                  </RadioGroup>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>근로자 수</InputLabel>
              <Controller
                name="workersCount"
                control={control}
                defaultValue={currentProject.workersCount}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    required
                    type="number"
                    margin="dense"
                  />
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>공정명-쉼표(,)로 구분</InputLabel>
              <Controller
                name="processNamesSerial"
                control={control}
                defaultValue={currentProject.processNamesSerial}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    required
                    color="primary"
                    margin="dense"
                    placeholder="ex) 공정명1, 공정명2, ..."
                    error={errors.processNamesSerial ? true : false}
                  />
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>지역</InputLabel>
              <Controller
                name="regionCode"
                control={control}
                defaultValue={currentProject.regionCode}
                render={({field}) => (
                  <Select
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    margin="dense"
                  >
                    {regionList.map((region, index) => (
                      <MenuItem key={index} value={index}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>관할관청</InputLabel>
              <Controller
                name="director"
                control={control}
                defaultValue={currentProject.director}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.director ? true : false}
                  />
                )}
              />
              <Typography variant="inherit" color="textSecondary">
                {errors.director?.message}
              </Typography>
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>현재등급</InputLabel>
              <Controller
                name="presentLevel"
                control={control}
                defaultValue={currentProject.presentLevel}
                render={({field}) => (
                  <Select
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    margin="dense"
                  >
                    <MenuItem value={0}>P</MenuItem>
                    <MenuItem value={1}>S</MenuItem>
                    <MenuItem value={2}>M+</MenuItem>
                    <MenuItem value={3}>M-</MenuItem>
                  </Select>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>목표등급</InputLabel>
              <Controller
                name="goalLevel"
                control={control}
                defaultValue={currentProject.goalLevel}
                render={({field}) => (
                  <Select
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    margin="dense"
                  >
                    <MenuItem value={0}>P</MenuItem>
                    <MenuItem value={1}>S</MenuItem>
                    <MenuItem value={2}>M+</MenuItem>
                    <MenuItem value={3}>M-</MenuItem>
                  </Select>
                )}
              />
            </Box>

            <Box className={classes.labelAlign}>
              <InputLabel>관련법규</InputLabel>
              <Controller
                name="law1"
                control={control}
                defaultValue={currentProject.law1}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.law1 ? true : false}
                  />
                )}
              />
              <Typography variant="inherit" color="textSecondary">
                {errors.law1?.message}
              </Typography>
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>점검표 양식 규정</InputLabel>
              <Controller
                name="law2"
                control={control}
                defaultValue={currentProject.law2}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.law2 ? true : false}
                  />
                )}
              />
              <Typography variant="inherit" color="textSecondary">
                {errors.law2?.message}
              </Typography>
            </Box>

            <Box className={classes.labelAlign}>
              <InputLabel>비교 포함여부</InputLabel>
              <Controller
                name="hasCompare"
                control={control}
                render={({field}) => (
                  <Checkbox
                    {...field}
                    defaultChecked={currentProject.hasCompare}
                    onChange={(e) => {
                      setValue<"hasCompare">("hasCompare", e.target.checked);
                    }}
                  />
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>비교방식</InputLabel>
              <Controller
                name="isCompareYear"
                control={control}
                render={({field}) => (
                  <RadioGroup
                    {...field}
                    className={classes.inputSize}
                    row
                    aria-label="isCompareYear"
                    value={isCompareYear}
                    onChange={(e) => {
                      setIsCompareYear(JSON.parse(e.target.value));
                    }}
                    // defaultValue={currentProject.target}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="연도" />
                    <FormControlLabel value={false} control={<Radio />} label="프로젝트" />
                  </RadioGroup>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>비교연도</InputLabel>
              <Controller
                name="compareYear"
                control={control}
                defaultValue={currentProject.compareYear}
                render={({field}) => (
                  <Select
                    {...field}
                    className={classes.inputSize}
                    variant="outlined"
                    margin="dense"
                  >
                    {yearList?.map((year, index) => (
                      <MenuItem key={index} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>비교방식</InputLabel>
              <Controller
                name="isCompareByScore"
                control={control}
                render={({field}) => (
                  <RadioGroup
                    {...field}
                    className={classes.inputSize}
                    row
                    aria-label="isCompareByScore"
                    value={isCompareByScore}
                    onChange={(e) => {
                      setIsCompareByScore(JSON.parse(e.target.value));
                    }}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="점수" />
                    <FormControlLabel value={false} control={<Radio />} label="건수" />
                  </RadioGroup>
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>점수 부여 없음</InputLabel>
              <Controller
                name="isNoPoint"
                control={control}
                render={({field}) => (
                  <Checkbox
                    {...field}
                    defaultChecked={currentProject.isNoPoint}
                    onChange={(e) => {
                      setValue<"isNoPoint">("isNoPoint", e.target.checked);
                    }}
                  />
                )}
              />
            </Box>
            {/* <Box className={classes.labelAlign}>
              <InputLabel>수동 점수 부여</InputLabel>
              <Controller
                name="isManualPoint"
                control={control}
                render={({ field }) => <Checkbox {...field} defaultChecked={currentProject.isManualPoint} onChange={(e) => { setValue<"isManualPoint">("isManualPoint", e.target.checked) }} />}
              />
            </Box> */}
            {/* <Box className={classes.labelAlign}>
              <InputLabel>도급업체 그래프 제외</InputLabel>
              <Controller
                name="isMinusContractor"
                defaultValue={currentProject.isMinusContractor}
                control={control}
                render={({ field }) => <Checkbox {...field} onChange={(e) => { setValue<"isMinusContractor">("isMinusContractor", e.target.checked) }}/>}
              />
            </Box> */}
            <Box className={classes.labelAlign}>
              <InputLabel>점검 시작일</InputLabel>
              {/* <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ko}> */}
              <Controller
                name="startDate"
                control={control}
                defaultValue={formatDate(currentProject.startDate) || ""}
                render={({field}) => (
                  <TextField
                    {...field}
                    type="date"
                    value={field.value || ""}
                    className={classes.inputSize}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.law1 ? true : false}
                  />
                )}
                // render={({ field: { ref, ...rest } }) => (
                //   <KeyboardDatePicker
                //     margin="normal"
                //     id="startDate"
                //     format="yyyy-MM-dd"
                //     KeyboardButtonProps={{
                //       "aria-label": "change edate",
                //     }}
                //     {...rest}
                //   />
                // )}
              />
              {/* </MuiPickersUtilsProvider> */}
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>점검 종료일</InputLabel>
              {/* <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ko}> */}
              <Controller
                name="endDate"
                control={control}
                defaultValue={formatDate(currentProject.endDate) || ""}
                render={({field}) => (
                  <TextField
                    {...field}
                    type="date"
                    value={field.value || ""}
                    className={classes.inputSize}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.law1 ? true : false}
                  />
                )}
                // render={({ field: { ref, ...rest } }) => (
                //   <KeyboardDatePicker
                //     margin="normal"
                //     id="endDate"
                //     format="yyyy-MM-dd"
                //     KeyboardButtonProps={{
                //       "aria-label": "change edate",
                //     }}
                //     {...rest}
                //   />
                // )}
              />
              {/* </MuiPickersUtilsProvider> */}
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>문서 첨부</InputLabel>
              <div>
                <input
                  accept="application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={fileLoaded}
                  style={{display: "none"}}
                  id="icon-button-photoImage2"
                  type="file"
                />
                <label htmlFor="icon-button-photoImage2">
                  <IconButton color="primary" aria-label="upload picture2" component="span">
                    <BackupIcon />
                  </IconButton>
                </label>
                <span>{fileName}</span>
              </div>
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>도움말</InputLabel>
              <Controller
                name="help"
                control={control}
                defaultValue={currentProject.help}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.help ? true : false}
                  />
                )}
              />
            </Box>
            <Box mt={4} mb={4} textAlign="center">
              <Button
                style={{width: "300px", height: "48px"}}
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
              >
                프로젝트 정보 저장
              </Button>
            </Box>
          </Box>
          <Box width="58%" className={classes.formGrid}>
            <AddParticipant />
            <AuditPlan editHandler={setEditSchedule} />
            {/* //note SmokingScully 추가 2022-02-19 */}
            {/* <Box className={classes.labelAlign}>
              <InputLabel>대상부서</InputLabel>
              <Controller
                name="scheduleDepartments"
                control={control}
                defaultValue={currentProject.scheduleDepartments}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.scheduleDepartments ? true : false}
                  />
                )}
              />
            </Box> */}
            <Box className={classes.labelAlign}>
              <InputLabel>감사내용</InputLabel>
              <Controller
                name="scheduleContents"
                control={control}
                defaultValue={currentProject.scheduleContents}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.scheduleContents ? true : false}
                  />
                )}
              />
            </Box>
            <Box className={classes.labelAlign}>
              <InputLabel>비고</InputLabel>
              <Controller
                name="scheduleNotes"
                control={control}
                defaultValue={currentProject.scheduleNotes}
                render={({field}) => (
                  <TextField
                    {...field}
                    className={classes.inputSize}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    color="primary"
                    margin="dense"
                    error={errors.scheduleNotes ? true : false}
                  />
                )}
              />
            </Box>
            <AuditMembers editHandler={setEditAuditMember} />
            <CompareProject />
          </Box>
        </Paper>
      </form>
    </>
  );
};

export default ManageProject;
